/**
 * The canonical English copy for every static label in the Ordo apps.
 *
 * THIS FILE IS THE SOURCE OF TRUTH. Every other locale is typed
 * `Record<MessageId, string>` against it, so omitting a key is a `tsc` error
 * (TS2741) on the `quality-i18n` job — the completeness guarantee is the TYPE,
 * deliberately, because this repo demonstrably ships check scripts wired into
 * no workflow.
 *
 * ## What belongs here
 *
 * A string CHOSEN BY CODE. A string STORED BY A USER (a menu item name, an
 * organization name) is content localization and belongs in
 * `MenuStringTranslation` — see `docs/i18n-translation-pipeline.md`.
 *
 * ## What does NOT belong here
 *
 * Anything `Intl` already knows: month names, weekday names, dates, numbers,
 * currency, percentages, list conjunctions. Bun ships FULL ICU (verified:
 * `es-US` long month → "septiembre de 2026", `Intl.ListFormat('es')` → "A, B y
 * C"), so those are free from the platform and a hand-authored table for any
 * of them is a bug waiting to drift. Format them at the leaf.
 *
 * Values are ICU MessageFormat. Put plural/select forms INSIDE the value
 * (`{n, plural, one {# item} other {# items}}`) rather than encoding them as
 * key suffixes — that is what keeps this type `Record<MessageId, string>` no
 * matter how many plural categories a future locale has.
 *
 * Seeded from an audit of the 41 keys in the retired `apps/ordo/client/src/lib/
 * i18n.ts`: these are the 20 that had a live call site. The other 21 were
 * authored for surfaces that moved on and were deliberately NOT carried over —
 * a catalog whose entire value is "every key exists in every locale" must not
 * be seeded with keys nothing renders.
 */

export const en = {
  // Avatar / user menu.
  'avatar.sign-out': 'Sign out',

  // Topbar.
  'topbar.menu': 'Open navigation',
  'topbar.search': 'Search',
  'topbar.notifications': 'Notifications',
  'topbar.inbox': 'Inbox',

  // Navigation search palette (⌘K).
  'search.placeholder': 'Search…',
  'search.empty': 'No results found.',
  'search.jumpTo': 'Jump to',

  // Chef kitchen surfaces — the three `ChefKitchenView` page titles.
  'nav.shopping-list': 'Ingredients',
  'nav.recipes': 'Recipes',
  'nav.deliverables': 'Deliverables',

  // Persona / view switchers.
  'switcher.personal': 'Personal',
  'switcher.work': 'Work',
  'switcher.family': 'Family',
  'switcher.school': 'School',
  'switcher.kitchen': 'Kitchen',
  'switcher.account': 'Account',
  'switcher.profile': 'Profile',
  'switcher.billing': 'Billing',

  // Common page states.
  'common.coming-soon.title': 'Coming soon',
  'common.coming-soon.body':
    "We're still building this view. Check back after the next release — or jump back to your home view in the meantime.",
  'common.coming-soon.back-home': 'Go to home',

  // ââ Menu calendar PDF (PROD-1293) ââââââââââââââââââââââââââââââââââââââ
  // Rendered into a parent-facing document by
  // `apps/ordo/server/src/managers/month-calendar-print.ts`. Weekday and month
  // names are NOT here â they come from `Intl`.
  'calendar.noService': 'NO SERVICE',
  'calendar.halfDay': 'HALF DAY',
  'calendar.aLaCarteSides': 'A La Carte Sides Offered',
  'calendar.milkSentence': 'All meals served with choice of milk ({options})',
  'calendar.continuation': ' (cont.)',
  'calendar.mealtimeMenuHeading': '{mealtime} Menu',
  // Joins two or more services in one heading ("Breakfast & Lunch Menu").
  // Deliberately a catalog key rather than `Intl.ListFormat`: ListFormat emits
  // "Breakfast and Lunch", which would change today's English output.
  'calendar.mealtimeJoin': ' & ',

  // Dietary legend. The GLYPHS (V / VGN / GF / DF / EF / Halal) stay English
  // in every locale â they are codes printed beside items, and changing them
  // per locale without changing the item badges would desynchronize the two.
  // Only the expansion translates, which is exactly a legend's job.
  'calendar.dietary.vegetarian': 'Vegetarian',
  'calendar.dietary.vegan': 'Vegan',
  'calendar.dietary.glutenFree': 'Gluten Free',
  'calendar.dietary.dairyFree': 'Dairy Free',
  'calendar.dietary.eggFree': 'Egg Free',

  // Mealtimes. Baked into `MonthCalendarData.mealtimeLabel` by the BUILDER,
  // not the renderer.
  'mealtime.breakfast': 'Breakfast',
  'mealtime.amSnack': 'Morning Snack',
  'mealtime.lunch': 'Lunch',
  'mealtime.snack': 'Snack',
  'mealtime.pmSnack': 'Afternoon Snack',
  'mealtime.supper': 'Supper',

  // US federal holidays. Computed constants with zero DB involvement, so they
  // are static labels rather than content.
  'holiday.newYears': "New Year's Day",
  'holiday.martinLutherKing': 'Martin Luther King Jr. Day',
  'holiday.presidents': "Presidents' Day",
  'holiday.memorial': 'Memorial Day',
  'holiday.juneteenth': 'Juneteenth',
  'holiday.independence': 'Independence Day',
  'holiday.labor': 'Labor Day',
  'holiday.indigenousPeoples': 'Indigenous Peoples’ Day',
  'holiday.veterans': 'Veterans Day',
  'holiday.thanksgiving': 'Thanksgiving Day',
  'holiday.christmas': 'Christmas Day',
} as const

export type MessageId = keyof typeof en
